import { useState } from 'react';
import { Brain, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { BDMAssistant } from './BDMAssistant';
import { projectId } from '../utils/supabase/info';
import { normalizeBdmExtraction } from '../utils/clientIntake';

/**
 * Add Client › Sales discovery notes, the AI half (RESTORE_PLAN R3.4, carrying
 * the old ConsultantPanel's Quick Extract and "Chat with AI Assistant"): pull
 * the industry, pain points, objectives, timeline, stakeholders, budget,
 * technical environment and a recommended platform out of the notes, or talk
 * them through with the BDM assistant, and show what was found. The parent
 * sends the result with the new company; its objectives seed the client's.
 */

export type BdmExtraction = Record<string, any>;

const LISTS: [string, string][] = [
  ['painPoints', 'Pain points'],
  ['objectives', 'Objectives'],
  ['stakeholders', 'Key stakeholders'],
  ['technicalEnvironment', 'Technical environment'],
];
const TEXTS: [string, string][] = [
  ['industry', 'Industry'],
  ['timeline', 'Timeline'],
  ['budgetIndicators', 'Budget context'],
  ['currentSystem', 'Current system'],
  ['teamSize', 'Team size'],
];

export function ExtractedInsights({ data, onClear }: { data: BdmExtraction; onClear?: () => void }) {
  return (
    <Alert className="border-blue-500 bg-blue-50" data-testid="bdm-insights">
      <Brain className="size-4 text-blue-600" />
      <AlertDescription>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-blue-800">AI-extracted insights (sent with the new client; its objectives become the client's first goals)</p>
            {onClear && (
              <Button type="button" size="sm" variant="ghost" aria-label="Discard insights" onClick={onClear}><X className="size-4" aria-hidden="true" /></Button>
            )}
          </div>
          {TEXTS.filter(([k]) => data[k]).map(([k, label]) => (
            <div key={k}><span className="text-gray-700">{label}:</span> <span className="text-gray-600">{String(data[k])}</span></div>
          ))}
          {LISTS.filter(([k]) => Array.isArray(data[k]) && data[k].length).map(([k, label]) => (
            <div key={k}>
              <span className="text-gray-700">{label}:</span>
              <ul className="ml-6 mt-1 list-disc text-gray-600">
                {data[k].map((item: string, i: number) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          ))}
          {data.recommendedPlatform && (
            <div className="mt-2 p-2 bg-green-100 rounded text-green-800">Recommended platform: {String(data.recommendedPlatform)}</div>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}

interface SalesNotesAssistProps {
  accessToken: string;
  notes: string;
  extracted: BdmExtraction | null;
  onExtracted: (data: BdmExtraction | null) => void;
}

export function SalesNotesAssist({ accessToken, notes, extracted, onExtracted }: SalesNotesAssistProps) {
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const hasNotes = notes.trim().length > 0;

  const quickExtract = async () => {
    setExtracting(true);
    setError('');
    try {
      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/extract-bdm-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ notes }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not read the notes');
      const clean = normalizeBdmExtraction(body.extracted);
      if (!clean) throw new Error('The AI found nothing to pull out of these notes');
      onExtracted(clean);
    } catch (e: any) {
      setError(e?.message || 'Could not read the notes');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setChatOpen(true)} disabled={!hasNotes}>
          <Brain className="mr-2 size-3" aria-hidden="true" />
          Chat with AI Assistant
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={quickExtract} disabled={!hasNotes || extracting}>
          {extracting ? <Loader2 className="mr-2 size-3 animate-spin" aria-hidden="true" /> : <Sparkles className="mr-2 size-3" aria-hidden="true" />}
          {extracting ? 'Analyzing...' : 'Quick Extract'}
        </Button>
      </div>
      {error && <p className="text-red-600" role="alert">{error}</p>}
      {extracted && <ExtractedInsights data={extracted} onClear={() => onExtracted(null)} />}

      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>BDM Discovery Assistant</DialogTitle>
            <DialogDescription>Talk the sales notes through; what it finds goes with the new client.</DialogDescription>
          </DialogHeader>
          {chatOpen && (
            <BDMAssistant
              accessToken={accessToken}
              initialNotes={notes}
              onDataExtracted={(data) => onExtracted(normalizeBdmExtraction(data))}
              onComplete={() => setChatOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
